// src/pages/Payment.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PaystackButton } from 'react-paystack';
import '../styles/payment.css';

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const Payment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { plan } = location.state || {};

  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [error, setError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const publicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY; // Your Paystack public key
  const currency = 'ZAR'; // Your currency code, must be enabled in Paystack dashboard
  const email = localStorage.getItem('email');

  const reference = `ref_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  const amount = plan && plan.paystackAmount ? plan.paystackAmount : 0;

  useEffect(() => {
    if (!plan || !plan.paystackAmount) {
      navigate('/checkout');
      return;
    }
    if (!email || !isValidEmail(email)) {
      setError('Invalid or missing email address. Please log in again.');
      setPaymentStatus('failed');
      return;
    }
    if (!amount || amount <= 0) {
      setError('Invalid payment amount.');
      setPaymentStatus('failed');
      return;
    }
    if (!currency || currency.trim() === '') {
      setError('Invalid or missing currency.');
      setPaymentStatus('failed');
      return;
    }
    if (!publicKey || publicKey.trim() === '') {
      setError('Paystack public key is missing.');
      setPaymentStatus('failed');
      return;
    }
    localStorage.removeItem('paymentStatus');
    localStorage.removeItem('reference');
  }, [plan, navigate, email, amount, currency, publicKey]);

  const handlePaystackSuccessAction = async (ref) => {
    setIsVerifying(true);
    try {
        setPaymentStatus('success');
        localStorage.setItem('paymentStatus', 'success');
        localStorage.setItem('paidPlan', JSON.stringify(plan));
        localStorage.setItem('reference', ref.reference);
        setError(null);
        navigate(`/payment-callback?reference=${ref.reference}&trxref=${ref.trxref}&status=success`);
    } catch (err) {
      setPaymentStatus('failed');
      setError(err?.message || JSON.stringify(err));
      console.error('Payment error:', err);
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePaystackCloseAction = () => {
    setPaymentStatus('failed');
    setError('Payment was cancelled by user.');
  };

  const componentProps = {
    email: email || "",
    amount: amount,
    currency,
    publicKey,
    reference,
    metadata: {
      planId: plan?.id || '',
      customerEmail: email,
    },
    text: 'Pay Now',
    onSuccess: handlePaystackSuccessAction,
    onClose: handlePaystackCloseAction,
  };

  const handleDashboardNavigation = () => {
    if (!plan || !plan.features) {
      navigate('/checkout');
      return;
    }
    if (plan.features.includes('Venting Room')) {
      navigate('/venting-room-dashboard');
    } else if (plan.features.includes('Entrepreneurship')) {
      navigate('/entrepreneurship-dashboard');
    } else if (plan.features.includes('STEM Subjects')) {
      navigate('/stem-subjects-dashboard');
    } else {
      navigate('/checkout');
    }
  };

  return (
    <div className="payment-container">
      <h1>Payment Processing</h1>

      {paymentStatus === 'pending' && !error && (
        <div>
          <p>Please complete your payment.</p>
          <PaystackButton {...componentProps} />
        </div>
      )}

      {isVerifying && (
        <div>
          <p>Verifying your payment, please wait...</p>
          <div className="loader"></div>
        </div>
      )}

      {paymentStatus === 'success' && !isVerifying && (
        <div>
          <p>Payment successful!</p>
          <button onClick={handleDashboardNavigation}>Go to Dashboard</button>
        </div>
      )}

      {(paymentStatus === 'failed' || error) && !isVerifying && (
        <div>
          <p>Payment failed. Please try again.</p>
          {error && <p className="error-message">{error}</p>}
          <button onClick={() => navigate('/checkout')}>Go to Checkout</button>
        </div>
      )}
    </div>
  );
};

export default Payment;
